import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { 
  getBoostPackages, 
  createBoostPackage, 
  updateBoostPackage, 
  deleteBoostPackage 
} from '../../services/api';
import { 
  FiSearch, 
  FiFilter, 
  FiRefreshCw, 
  FiEdit2,
  FiTrash2,
  FiToggleLeft,
  FiToggleRight,
  FiPlus,
  FiTrendingUp,
  FiPackage,
  FiDollarSign,
  FiCalendar,
  FiStar,
  FiShoppingBag,
  FiX,
  FiCheck,
  FiAlertCircle,
  FiArrowLeft,
  FiArrowRight
} from 'react-icons/fi';

function BoostPackages({ admin }) {
  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [filters, setFilters] = useState({
    status: '',
    search: '',
    page: 1,
    limit: 20
  });
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
    pages: 0
  });
  const [showFilters, setShowFilters] = useState(false);

  const navigate = useNavigate();

  useEffect(() => {
    fetchPackages();
  }, [filters, pagination.page]);

  const fetchPackages = async () => {
    try {
      setLoading(true);
      setError('');
      const params = {
        page: pagination.page,
        limit: pagination.limit,
        ...filters
      };

      Object.keys(params).forEach(key => {
        if (params[key] === '') {
          delete params[key];
        }
      });

      const response = await getBoostPackages(params);
      if (response.success) {
        setPackages(response.data.packages || []);
        setPagination(prev => ({
          ...prev,
          total: response.data.pagination?.total || 0,
          pages: response.data.pagination?.pages || 0
        }));
      } else {
        setError(response.message || 'Failed to load packages');
      }
    } catch (err) {
      setError('Failed to load packages: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const handleDelete = async (packageId, packageName) => {
    if (!window.confirm(`Are you sure you want to delete "${packageName}"? This action cannot be undone.`)) {
      return;
    }

    try {
      const response = await deleteBoostPackage(packageId);
      if (response.success) {
        setSuccessMessage(`Package "${packageName}" deleted successfully!`);
        setTimeout(() => setSuccessMessage(''), 3000);
        fetchPackages();
      } else {
        throw new Error(response.message || 'Delete failed');
      }
    } catch (err) {
      setError('Failed to delete package: ' + err.message);
    }
  };

  const handleStatusToggle = async (pkg) => {
    try {
      const response = await updateBoostPackage(pkg.package_id, {
        is_active: !pkg.is_active
      });
      if (response.success) {
        setSuccessMessage(`Package ${!pkg.is_active ? 'activated' : 'deactivated'} successfully!`);
        setTimeout(() => setSuccessMessage(''), 3000);
        fetchPackages();
      } else {
        throw new Error(response.message || 'Update failed');
      }
    } catch (err) {
      setError('Failed to update package: ' + err.message);
    }
  };

  const handlePageChange = (newPage) => {
    setPagination(prev => ({ ...prev, page: newPage }));
  };

  const getStatusBadge = (isActive) => {
    return isActive ? (
      <span className="px-3 py-1 bg-green-900/30 text-green-400 text-sm font-medium rounded-full border border-green-800/50 flex items-center">
        <FiCheck className="mr-1" />
        Active
      </span>
    ) : (
      <span className="px-3 py-1 bg-red-900/30 text-red-400 text-sm font-medium rounded-full border border-red-800/50 flex items-center">
        <FiX className="mr-1" />
        Inactive
      </span>
    );
  };

  const getPopularBadge = (isPopular) => {
    return isPopular ? (
      <span className="px-2 py-1 bg-[#ff660e]/20 text-[#ff660e] text-xs font-medium rounded-full border border-[#ff660e]/30 flex items-center">
        <FiStar className="mr-1" size={12} />
        Popular
      </span>
    ) : (
      <span className="px-2 py-1 bg-gray-700 text-gray-400 text-xs font-medium rounded-full border border-gray-600">
        Standard
      </span>
    );
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-NG', {
      style: 'currency',
      currency: 'NGN',
      minimumFractionDigits: 0
    }).format(amount || 0);
  };

  const clearFilters = () => {
    setFilters({
      status: '',
      search: '',
      page: 1,
      limit: 20
    });
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  // Loading Skeleton
  if (loading && packages.length === 0) {
    return (
      <div className="min-h-screen bg-gray-900 p-6">
        <div className="animate-pulse">
          {/* Header Skeleton */}
          <div className="mb-6">
            <div className="h-8 bg-gray-700 rounded w-1/4 mb-2"></div>
            <div className="h-4 bg-gray-700 rounded w-1/2"></div>
          </div>

          {/* Table Skeleton */}
          <div className="bg-gray-800 rounded-xl border border-gray-700 overflow-hidden">
            {[...Array(5)].map((_, i) => (
              <div key={i} className="p-6 border-b border-gray-700">
                <div className="flex justify-between items-center">
                  <div className="space-y-2">
                    <div className="h-4 bg-gray-700 rounded w-48"></div>
                    <div className="h-3 bg-gray-700 rounded w-32"></div>
                  </div>
                  <div className="h-6 bg-gray-700 rounded w-20"></div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-full bg-gray-900 p-4 md:p-6">
      {/* Header */}
      <div className="mb-6">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-white">
              <span className="text-[#ff660e]">Boost</span> Packages
            </h1>
            <p className="text-gray-400 mt-1">Manage listing boost packages and pricing for sellers</p>
          </div>
          <div className="flex gap-3">
            <button
              onClick={fetchPackages}
              className="px-4 py-2.5 bg-gray-800 text-white border border-gray-700 rounded-lg hover:bg-gray-700 hover:border-[#ff660e] transition-all duration-200 font-medium flex items-center hover:scale-[1.02]"
            >
              <FiRefreshCw className="mr-2" />
              Refresh
            </button>
            <Link
              to="/admin/boost/packages/new"
              className="px-4 py-2.5 bg-gradient-to-r from-[#ff660e] to-orange-500 text-white rounded-lg hover:from-[#ff5500] hover:to-orange-600 transition-all duration-200 font-medium flex items-center hover:scale-[1.02] shadow-lg"
            >
              <FiPlus className="mr-2" />
              Create Package
            </Link>
          </div>
        </div>
      </div>

      {/* Success Message */}
      {successMessage && (
        <div className="mb-6 bg-green-900/20 border border-green-800/50 rounded-xl p-4 flex items-center justify-between animate-fade-in backdrop-blur-sm">
          <div className="flex items-center space-x-3">
            <FiCheck className="text-green-400 text-xl" />
            <p className="text-green-300 font-medium">{successMessage}</p>
          </div>
          <button 
            onClick={() => setSuccessMessage('')}
            className="text-green-400 hover:text-green-300 text-xl"
          >
            ×
          </button>
        </div>
      )}

      {/* Error Alert */}
      {error && (
        <div className="mb-6 bg-red-900/20 border border-red-800/50 rounded-xl p-4 flex items-center justify-between animate-fade-in backdrop-blur-sm">
          <div className="flex items-center space-x-3">
            <FiAlertCircle className="text-red-400 text-xl" />
            <p className="text-red-300">{error}</p>
          </div>
          <button 
            onClick={() => setError('')}
            className="text-red-400 hover:text-red-300 text-xl"
          >
            ×
          </button>
        </div>
      )}

      {/* Filters Card */}
      <div className="bg-gray-800 rounded-xl border border-gray-700 mb-6">
        <div className="p-4 border-b border-gray-700">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <FiFilter className="text-[#ff660e]" />
              <h3 className="font-medium text-white">Filters</h3>
            </div>
            <button
              onClick={() => setShowFilters(!showFilters)}
              className="text-sm text-[#ff660e] hover:text-orange-400 font-medium flex items-center transition-colors"
            >
              {showFilters ? 'Hide Filters' : 'Show Filters'}
            </button>
          </div>
        </div>

        {showFilters && (
          <div className="p-4 md:p-6">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              {/* Search */}
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-300 mb-2">Search Packages</label>
                <div className="relative">
                  <FiSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500" />
                  <input
                    type="text"
                    placeholder="Search by name or description..."
                    value={filters.search}
                    onChange={(e) => handleFilterChange('search', e.target.value)}
                    className="w-full pl-10 pr-3 py-2.5 bg-gray-700 border border-gray-600 rounded-lg focus:ring-2 focus:ring-[#ff660e] focus:border-transparent transition-all duration-200 text-white placeholder-gray-500"
                  />
                </div>
              </div>

              {/* Status Filter */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Status</label>
                <select
                  value={filters.status}
                  onChange={(e) => handleFilterChange('status', e.target.value)}
                  className="w-full px-3 py-2.5 bg-gray-700 border border-gray-600 rounded-lg focus:ring-2 focus:ring-[#ff660e] focus:border-transparent transition-all duration-200 text-white"
                >
                  <option value="">All Status</option>
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                </select>
              </div>

              {/* Results Count */}
              <div className="flex items-end">
                <div className="text-sm text-gray-400">
                  {pagination.total} package{pagination.total !== 1 ? 's' : ''} found
                </div>
              </div>
            </div>

            {/* Filter Actions */}
            <div className="flex justify-between items-center mt-6 pt-6 border-t border-gray-700">
              <button
                onClick={clearFilters}
                className="px-4 py-2 text-gray-400 hover:text-gray-300 text-sm font-medium hover:bg-gray-700 rounded-lg transition-colors"
              >
                Clear All Filters
              </button>
              <div className="flex gap-3">
                <button
                  onClick={() => setShowFilters(false)}
                  className="px-4 py-2 text-gray-300 bg-gray-700 border border-gray-600 rounded-lg hover:bg-gray-600 transition-colors font-medium hover:scale-[1.02]"
                >
                  Cancel
                </button>
                <button
                  onClick={fetchPackages}
                  className="px-4 py-2 bg-gradient-to-r from-[#ff660e] to-orange-500 text-white rounded-lg hover:from-[#ff5500] hover:to-orange-600 transition-colors font-medium flex items-center hover:scale-[1.02]"
                >
                  <FiFilter className="mr-2" />
                  Apply Filters
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Packages Table */}
      <div className="bg-gray-800 rounded-xl border border-gray-700 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-700">
            <thead className="bg-gray-900">
              <tr>
                <th className="px-6 py-4 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                  Package Details
                </th>
                <th className="px-6 py-4 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                  Duration & Price
                </th>
                <th className="px-6 py-4 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                  Performance
                </th>
                <th className="px-6 py-4 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-4 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-gray-800 divide-y divide-gray-700">
              {packages.map((pkg) => (
                <tr key={pkg.package_id} className="hover:bg-gray-750 transition-colors duration-150 group">
                  <td className="px-6 py-4">
                    <div>
                      <div className="flex items-center space-x-2 mb-2">
                        <h3 className="text-sm font-semibold text-white group-hover:text-[#ff660e] transition-colors">
                          {pkg.name}
                        </h3>
                        {getPopularBadge(pkg.is_popular)}
                      </div>
                      {pkg.description && (
                        <p className="text-sm text-gray-400 mt-1">{pkg.description}</p>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="space-y-2">
                      <div className="flex items-center space-x-2">
                        <div className="px-2 py-1 bg-blue-900/30 text-blue-400 text-sm font-medium rounded border border-blue-800/50 flex items-center">
                          <FiCalendar className="mr-1" />
                          {pkg.duration_days} days
                        </div>
                        {pkg.max_listings && (
                          <div className="px-2 py-1 bg-purple-900/30 text-purple-400 text-sm font-medium rounded border border-purple-800/50 flex items-center">
                            <FiShoppingBag className="mr-1" />
                            {pkg.max_listings} listings
                          </div>
                        )}
                      </div>
                      <div className="flex items-center space-x-2">
                        <span className="text-xl font-bold text-white">
                          {formatCurrency(pkg.price)}
                        </span>
                        {pkg.original_price && pkg.original_price > pkg.price && (
                          <span className="text-sm text-gray-500 line-through">
                            {formatCurrency(pkg.original_price)}
                          </span>
                        )}
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="space-y-1">
                      <div className="flex items-center space-x-2">
                        <FiTrendingUp className="text-green-400" />
                        <span className="text-sm text-gray-300">
                          Purchases: <strong className="text-white">{pkg.total_purchases || 0}</strong>
                        </span>
                      </div>
                      <div className="flex items-center space-x-2">
                        <FiDollarSign className="text-blue-400" />
                        <span className="text-sm text-gray-300">
                          Revenue: <strong className="text-white">{formatCurrency(pkg.total_revenue || 0)}</strong>
                        </span>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center space-x-2">
                      {getStatusBadge(pkg.is_active)}
                      <button
                        onClick={() => handleStatusToggle(pkg)}
                        className="text-gray-400 hover:text-white transition-colors"
                        title={pkg.is_active ? 'Deactivate' : 'Activate'}
                      >
                        {pkg.is_active ? (
                          <FiToggleRight className="text-green-400 text-xl" />
                        ) : (
                          <FiToggleLeft className="text-gray-500 text-xl" />
                        )}
                      </button>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex space-x-3">
                      <Link
                        to={`/admin/boost/packages/edit/${pkg.package_id}`}
                        className="text-blue-400 hover:text-blue-300 transition-colors flex items-center group"
                        title="Edit Package"
                      >
                        <FiEdit2 className="mr-1" />
                        <span className="text-sm font-medium">Edit</span>
                      </Link>
                      <button
                        onClick={() => handleDelete(pkg.package_id, pkg.name)}
                        disabled={pkg.total_purchases > 0}
                        className={`transition-colors flex items-center group ${
                          pkg.total_purchases > 0 
                            ? 'text-gray-600 cursor-not-allowed' 
                            : 'text-red-400 hover:text-red-300'
                        }`}
                        title={pkg.total_purchases > 0 ? 'Cannot delete package with purchases' : 'Delete Package'}
                      >
                        <FiTrash2 className="mr-1" />
                        <span className="text-sm font-medium">Delete</span>
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Empty State */}
        {packages.length === 0 && !loading && (
          <div className="text-center py-16">
            <div className="text-gray-600 text-8xl mb-6">📦</div>
            <h3 className="text-xl font-medium text-white mb-2">No boost packages found</h3>
            <p className="text-gray-400 mb-6">Try adjusting your search or create a new package</p>
            <Link
              to="/admin/boost/packages/new"
              className="px-6 py-2.5 bg-gradient-to-r from-[#ff660e] to-orange-500 text-white rounded-lg hover:from-[#ff5500] hover:to-orange-600 transition-colors font-medium hover:scale-[1.02] inline-flex items-center"
            >
              <FiPlus className="mr-2" />
              Create First Package
            </Link>
          </div>
        )}

        {/* Pagination */}
        {pagination.pages > 1 && (
          <div className="bg-gray-900 px-6 py-4 border-t border-gray-700">
            <div className="flex flex-col md:flex-row items-center justify-between gap-4">
              <div className="text-sm text-gray-400">
                Showing <span className="font-semibold text-white">{(pagination.page - 1) * pagination.limit + 1}</span> to{' '}
                <span className="font-semibold text-white">
                  {Math.min(pagination.page * pagination.limit, pagination.total)}
                </span>{' '}
                of <span className="font-semibold text-white">{pagination.total}</span> packages
              </div>
              <div className="flex items-center space-x-2">
                <button
                  onClick={() => handlePageChange(pagination.page - 1)}
                  disabled={pagination.page === 1}
                  className="px-4 py-2 border border-gray-700 rounded-lg text-sm font-medium text-gray-400 bg-gray-800 hover:bg-gray-700 hover:text-white hover:border-[#ff660e] disabled:opacity-50 disabled:cursor-not-allowed flex items-center transition-all duration-200 hover:scale-[1.02]"
                >
                  <FiArrowLeft className="mr-2" />
                  Previous
                </button>
                
                <div className="flex items-center space-x-1">
                  {Array.from({ length: Math.min(5, pagination.pages) }, (_, i) => {
                    let pageNum;
                    if (pagination.pages <= 5) {
                      pageNum = i + 1;
                    } else if (pagination.page <= 3) {
                      pageNum = i + 1;
                    } else if (pagination.page >= pagination.pages - 2) {
                      pageNum = pagination.pages - 4 + i;
                    } else {
                      pageNum = pagination.page - 2 + i;
                    }
                    
                    return (
                      <button
                        key={pageNum}
                        onClick={() => handlePageChange(pageNum)}
                        className={`w-10 h-10 rounded-lg text-sm font-medium transition-all duration-200 hover:scale-105 ${
                          pagination.page === pageNum
                            ? 'bg-[#ff660e] text-white shadow-lg'
                            : 'border border-gray-700 text-gray-400 hover:bg-gray-700 hover:text-white'
                        }`}
                      >
                        {pageNum}
                      </button>
                    );
                  })}
                </div>
                
                <button
                  onClick={() => handlePageChange(pagination.page + 1)}
                  disabled={pagination.page === pagination.pages}
                  className="px-4 py-2 border border-gray-700 rounded-lg text-sm font-medium text-gray-400 bg-gray-800 hover:bg-gray-700 hover:text-white hover:border-[#ff660e] disabled:opacity-50 disabled:cursor-not-allowed flex items-center transition-all duration-200 hover:scale-[1.02]"
                >
                  Next
                  <FiArrowRight className="ml-2" />
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Quick Stats */}
      {packages.length > 0 && (
        <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-gray-800 rounded-xl border border-gray-700 p-5">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-400 text-sm font-medium">Total Packages</p>
                <p className="text-2xl font-bold text-white mt-1">{pagination.total}</p>
              </div>
              <div className="w-12 h-12 rounded-lg bg-blue-900/30 flex items-center justify-center">
                <FiPackage className="text-blue-400 text-2xl" />
              </div>
            </div>
          </div>
          <div className="bg-gray-800 rounded-xl border border-gray-700 p-5">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-400 text-sm font-medium">Active Packages</p>
                <p className="text-2xl font-bold text-green-400 mt-1">
                  {packages.filter(p => p.is_active).length}
                </p>
              </div>
              <div className="w-12 h-12 rounded-lg bg-green-900/30 flex items-center justify-center">
                <FiTrendingUp className="text-green-400 text-2xl" />
              </div>
            </div>
          </div>
          <div className="bg-gray-800 rounded-xl border border-gray-700 p-5">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-400 text-sm font-medium">Total Revenue</p>
                <p className="text-2xl font-bold text-[#ff660e] mt-1">
                  {formatCurrency(packages.reduce((sum, p) => sum + (p.total_revenue || 0), 0))}
                </p>
              </div>
              <div className="w-12 h-12 rounded-lg bg-[#ff660e]/20 flex items-center justify-center">
                <FiDollarSign className="text-[#ff660e] text-2xl" />
              </div>
            </div>
          </div>
        </div>
      )}

      {/* CSS for animations */}
      <style jsx>{`
        @keyframes fade-in {
          from { opacity: 0; transform: translateY(-10px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .animate-fade-in {
          animation: fade-in 0.3s ease-out;
        }
      `}</style>
    </div>
  );
}

export default BoostPackages;